import React, { forwardRef } from 'react';
import {
  Link as LinkBase,
  NavLink as NavLinkBase,
  Navigate as NavigateBase,
  useLocation,
  useNavigate as useNavigateBase,
} from 'react-router-dom';

const TRANSIENT_QUERY_PARAMETERS = ['emailId', 'taskId'];

type PathWithQuerystring = `${string}?${string}`;
type PathWithoutQuerystring<P extends string> = P extends PathWithQuerystring ? never : P;

export const useHistory = () => {
  const navigate = useNavigateBase();
  const { search } = useLocation();

  const getPersistedQueryParams = () => {
    const queryParams = new URLSearchParams(search);
    return Array.from(queryParams.entries()).filter(([key, _value]) => !TRANSIENT_QUERY_PARAMETERS.includes(key));
  };

  const historyWrapper =
    (fn: (url: string, { replace }: { replace?: boolean }) => void) =>
    <P extends string>(
      url: PathWithoutQuerystring<P>,
      {
        queryParams,
        forceClearQuery,
        replace,
      }: { queryParams?: Record<string, string>; forceClearQuery?: boolean; replace?: boolean } = {
        replace: false,
        queryParams: {},
        forceClearQuery: false,
      }
    ) => {
      const newParams = forceClearQuery
        ? new URLSearchParams([...Object.entries(queryParams ?? {})])
        : new URLSearchParams([...getPersistedQueryParams(), ...Object.entries(queryParams ?? {})]);
      const queryString = Array.from(newParams.entries()).length > 0 ? '?' + newParams.toString() : '';

      return fn(url + queryString, { replace });
    };

  const addQueryToUrl = (to: string) => {
    const [url, search] = to.split('?');

    const persistedQueryParams = getPersistedQueryParams();
    const urlParams = search ? Array.from(new URLSearchParams(search).entries()) : [];

    const newParams = new URLSearchParams([...persistedQueryParams, ...urlParams]);
    const queryString = Array.from(newParams.entries()).length > 0 ? '?' + newParams.toString() : '';

    return url + queryString;
  };

  const Link = forwardRef<
    HTMLAnchorElement,
    Omit<React.ComponentProps<typeof LinkBase>, 'to'> & { to: PathWithoutQuerystring<string> }
  >(({ to, ...params }, ref) => <LinkBase to={addQueryToUrl(to)} {...params} ref={ref} />);

  const NavLink = forwardRef<
    HTMLAnchorElement,
    Omit<React.ComponentProps<typeof NavLinkBase>, 'to'> & { to: PathWithoutQuerystring<string> }
  >(({ to, ...params }, ref) => <NavLinkBase to={addQueryToUrl(to)} {...params} ref={ref} />);

  const Navigate = forwardRef<
    never,
    Omit<React.ComponentProps<typeof NavigateBase>, 'to'> & { to: PathWithoutQuerystring<string> }
  >(({ to, ...params }, ref) => <NavigateBase to={addQueryToUrl(to)} {...params} />);

  return {
    navigate: historyWrapper(navigate),
    Link,
    NavLink,
    Navigate,
  };
};
